/** Reseller-specific dashboard layout with navigation and stats. */
import React, { ReactNode, useState, useEffect } from "react";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Container,
  CssBaseline,
  IconButton,
  Divider,
  Card,
  CardContent,
  Grid,
  CircularProgress,
} from "@mui/material";
import {
  Assessment as ReportsIcon,
  RateReview as RatesIcon,
  Dashboard as DashboardIcon,
  People as AccountsIcon,
  Description as CdrsIcon,
  SyncAlt as TransferIcon,
  Person as ProfileIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon,
  AttachMoney as BalanceIcon,
  TrendingUp as MarginIcon,
  Phone as CallsIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { default as apiClient, authApi, clearResellerToken } from "../../api/client";
import ConfirmDialog from "../common/ConfirmDialog";
import { formatCurrency } from "../../utils/format";

const DRAWER_WIDTH = 280;

interface ResellerDashboardLayoutProps {
  children: ReactNode;
}

/** Reseller navigation menu items. */
const RESELLER_NAV_ITEMS = [
  { text: "Dashboard", icon: <DashboardIcon />, path: "/reseller/dashboard" },
  { text: "Accounts", icon: <AccountsIcon />, path: "/reseller/accounts" },
  { text: "CDRs", icon: <CdrsIcon />, path: "/reseller/cdrs" },
  { text: "Rates", icon: <RatesIcon />, path: "/reseller/rates" },
  { text: "Reports", icon: <ReportsIcon />, path: "/reseller/reports" },
  { text: "Balance Transfer", icon: <TransferIcon />, path: "/reseller/transfer" },
  { text: "Profile", icon: <ProfileIcon />, path: "/reseller/profile" },
];

/** Main reseller dashboard layout component. */
export default function ResellerDashboardLayout({ children }: ResellerDashboardLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [resellerName, setResellerName] = useState("");
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadResellerInfo();
  }, []);

  /** K: use the shared axios client (proxy + cookies) instead of a raw
   * fetch with a hardcoded localhost URL. */
  const loadResellerInfo = async () => {
    try {
      const username = localStorage.getItem("reseller_username");
      setResellerName(username || "Reseller");

      const response = await apiClient.get("/api/v1/resellers/me");
      setBalance(response.data?.balance || 0);
    } catch (error) {
      console.error("Failed to load reseller info:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  /** B5: server-side logout with confirmation. */
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const handleLogoutConfirmed = async () => {
    setLoggingOut(true);
    try {
      await authApi.resellerLogout();
    } catch {
      // best-effort cookie cleanup server-side
    }
    clearResellerToken();
    setLoggingOut(false);
    setLogoutOpen(false);
    navigate("/reseller/login");
  };

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div" sx={{ fontWeight: "bold" }}>
          Reseller Portal
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {RESELLER_NAV_ITEMS.map((item) => (
          <ListItem
            button
            key={item.text}
            onClick={() => navigate(item.path)}
            selected={location.pathname === item.path}
            sx={{
              "&.Mui-selected": {
                backgroundColor: "primary.main",
                color: "white",
                "&:hover": {
                  backgroundColor: "primary.dark",
                },
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: location.pathname === item.path ? "inherit" : "inherit",
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { sm: `${DRAWER_WIDTH}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open navigation menu"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Reseller Portal — {resellerName}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {loading ? (
              <CircularProgress size={24} sx={{ color: "white" }} />
            ) : (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
                aria-label={`Account balance ${formatCurrency(balance)}`}
              >
                <BalanceIcon fontSize="small" />
                <Typography variant="body2">{formatCurrency(balance)}</Typography>
              </Box>
            )}
            <IconButton
              color="inherit"
              onClick={() => setLogoutOpen(true)}
              title="Logout"
              aria-label="Logout"
            >
              <LogoutIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: DRAWER_WIDTH,
            },
          }}
        >
          {drawer}
        </Drawer>
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: DRAWER_WIDTH,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          minHeight: "100vh",
          backgroundColor: "background.default",
        }}
      >
        <Toolbar />
        <Container maxWidth="xl" sx={{ mt: 2 }}>
          {children}
        </Container>
      </Box>

      {/* B5: logout confirmation */}
      <ConfirmDialog
        open={logoutOpen}
        title="Sign out?"
        message="You will be returned to the reseller login screen."
        confirmLabel="Sign out"
        onClose={() => setLogoutOpen(false)}
        onConfirm={handleLogoutConfirmed}
        loading={loggingOut}
      />
    </Box>
  );
}
